import { useState, useEffect, type FormEvent } from "react";
import type {
  ReputationSettingsData,
  LocationSummary,
  GoogleStatus,
  GoogleAccount,
  GoogleLocation,
} from "../types";
import type { PlatformApiClient } from "../../api";
import type { OrganizationContext } from "../../types";

const SETTINGS_SECTIONS = [
  { id: "negocio", label: "Negocio", icon: "business", desc: "Identidad y datos del local" },
  { id: "google", label: "Google Business Profile", icon: "google", desc: "Conexión y sincronización" },
  { id: "notificaciones", label: "Notificaciones", icon: "notifications", desc: "Alertas de reputación" },
] as const;

type SectionId = (typeof SETTINGS_SECTIONS)[number]["id"];

export function SettingsView({
  settings: initialSettings,
  location: initialLocation,
  googleStatus: initialGoogleStatus,
  googleAccounts: initialGoogleAccounts = [],
  googleLocations: initialGoogleLocations = [],
  organization,
  initialSection = "negocio",
  slug,
  api,
  base,
  navigate,
  reload,
}: {
  settings: ReputationSettingsData;
  location: LocationSummary | null;
  googleStatus: GoogleStatus | null;
  googleAccounts?: GoogleAccount[];
  googleLocations?: GoogleLocation[];
  organization: OrganizationContext;
  initialSection?: string;
  slug: string;
  api: PlatformApiClient;
  base: string;
  navigate: (path: string) => void;
  reload: () => void;
}) {
  const [activeSection, setActiveSection] = useState<SectionId>(() => {
    const found = SETTINGS_SECTIONS.find((s) => s.id === initialSection);
    return found ? found.id : "negocio";
  });

  useEffect(() => {
    const found = SETTINGS_SECTIONS.find((s) => s.id === initialSection);
    if (found && found.id !== activeSection) {
      setActiveSection(found.id);
    }
  }, [initialSection]);

  // Business Profile Form
  const [businessDisplayName, setBusinessDisplayName] = useState(
    initialSettings.business_display_name || initialLocation?.name || ""
  );
  const [address, setAddress] = useState(initialLocation?.address || "");
  const [phone, setPhone] = useState(initialLocation?.phone || "");
  const [whatsapp, setWhatsapp] = useState(initialLocation?.whatsapp || "");
  const [website, setWebsite] = useState(initialLocation?.website || "");

  // Notification Form
  const [alertsEnabled, setAlertsEnabled] = useState(
    Boolean(initialSettings.alerts_enabled)
  );
  const [notificationEmail, setNotificationEmail] = useState(
    initialSettings.notification_email || ""
  );

  // States
  const [busy, setBusy] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  function notify(msg: string) {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  async function handleSaveBusiness(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMessage(null);
    try {
      await api.reputation(slug, "/settings", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          business_display_name: businessDisplayName.trim() || null,
        }),
      });

      if (initialLocation?.id) {
        await api.reputation(
          slug,
          `/locations/${encodeURIComponent(initialLocation.id)}/profile`,
          {
            method: "PATCH",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              address: address.trim() || null,
              phone: phone.trim() || null,
              whatsapp: whatsapp.trim() || null,
              website: website.trim() || null,
            }),
          }
        );
      }
      notify("Datos del negocio actualizados.");
      reload();
    } catch (cause) {
      setErrorMessage(
        cause instanceof Error ? cause.message : "No se pudieron guardar los cambios."
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleSaveNotifications(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErrorMessage(null);
    try {
      await api.reputation(slug, "/settings", {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          alerts_enabled: alertsEnabled,
          notification_email: notificationEmail.trim() || null,
        }),
      });
      notify("Preferencias de notificación guardadas.");
      reload();
    } catch (cause) {
      setErrorMessage(
        cause instanceof Error ? cause.message : "Error al guardar notificaciones."
      );
    } finally {
      setBusy(false);
    }
  }

  // Google actions
  async function handleGoogleConnect() {
    setBusy(true);
    setErrorMessage(null);
    try {
      const res = await api.reputation<{
        adapter?: string;
        mock_complete?: { body: unknown };
        authorize_url?: string;
      }>(slug, "/google/connect/start", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: "{}",
      });

      if (res.adapter === "mock" && res.mock_complete) {
        await api.reputation(slug, "/google/connect/callback", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(res.mock_complete.body),
        });
        notify("Google Business Profile conectado (modo demo).");
        reload();
      } else if (res.authorize_url) {
        window.location.href = res.authorize_url;
      }
    } catch (cause) {
      setErrorMessage(
        cause instanceof Error ? cause.message : "Error al conectar Google Business Profile."
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogleSync() {
    setBusy(true);
    setErrorMessage(null);
    try {
      const primaryLoc = initialGoogleLocations.find((l) => l.is_primary) || initialGoogleLocations[0];
      const res = await api.reputation<{
        reviews_upserted?: number;
        reviews_fetched?: number;
        provider_total_review_count?: number | null;
        pages_fetched?: number;
      }>(
        slug,
        "/google/sync/reviews",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            ...(primaryLoc?.location_id ? { location_id: primaryLoc.location_id } : {}),
            full_history: true,
          }),
        }
      );
      const providerTotal = res.provider_total_review_count;
      notify(
        providerTotal != null
          ? `Sincronización completa: ${providerTotal} reseñas en Google · ${res.reviews_upserted ?? 0} nuevas o actualizadas · ${res.pages_fetched ?? 0} páginas.`
          : `Sincronización completa: ${res.reviews_fetched ?? 0} reseñas leídas · ${res.reviews_upserted ?? 0} nuevas o actualizadas.`
      );
      reload();
    } catch (cause) {
      setErrorMessage(
        cause instanceof Error ? cause.message : "Error al sincronizar reseñas con Google."
      );
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogleDisconnect() {
    if (!window.confirm("¿Seguro que querés desconectar tu cuenta de Google Business Profile?")) return;
    setBusy(true);
    setErrorMessage(null);
    try {
      await api.reputation(slug, "/google/disconnect", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: "{}",
      });
      notify("Google Business Profile desconectado.");
      reload();
    } catch (cause) {
      setErrorMessage(
        cause instanceof Error ? cause.message : "Error al desconectar Google."
      );
    } finally {
      setBusy(false);
    }
  }

  const isConnected = initialGoogleStatus?.status === "connected";
  const googleConnectionLabel = isConnected
    ? "Conectado"
    : initialGoogleStatus?.status === "expired" || initialGoogleStatus?.status === "needs_reauth"
      ? "Reconectar Google"
      : initialGoogleStatus?.status === "connecting"
        ? "Conexión en curso"
        : initialGoogleStatus?.status === "error"
          ? "Revisar conexión"
          : "Requiere conexión";
  const googleLocationsCount = initialGoogleLocations.length;
  const googleAccountsCount = initialGoogleAccounts.length;

  return (
    <div className="rep-settings-view rep-layout-approved" data-layout="RB-CFG-D">
      <header className="page-heading">
        <p className="eyebrow">Configuración</p>
        <h1>Configuración de Review Boost</h1>
        <p>Definí los datos de tu local, conexión con Google y alertas de reputación.</p>
      </header>

      {successMessage && <div className="success-alert" role="status">{successMessage}</div>}
      {errorMessage && <div className="inline-alert" role="alert">{errorMessage}</div>}

      <div className="rep-settings-layout">
        {/* Navigation Tabs */}
        <aside className="rep-settings-sidebar" aria-label="Secciones de configuración">
          <nav className="rep-settings-nav">
            <p className="rep-settings-nav-kicker">Review Boost</p>
            {SETTINGS_SECTIONS.map((sec) => (
              <button
                key={sec.id}
                type="button"
                className={["rep-settings-nav-item", activeSection === sec.id ? "active" : ""].filter(Boolean).join(" ")}
                aria-pressed={activeSection === sec.id}
                onClick={() => {
                  setActiveSection(sec.id);
                  navigate(`${base}/settings/${sec.id}`);
                }}
              >
                <span className={`rep-settings-icon rep-settings-icon-${sec.icon}`} aria-hidden="true" />
                <div className="rep-settings-nav-text">
                  <strong>{sec.label}</strong>
                  <small>{sec.desc}</small>
                </div>
              </button>
            ))}
          </nav>
        </aside>

        {/* Section Content */}
        <section className="rep-settings-content" aria-label="Opciones de configuración de Review Boost">
          {/* 1. NEGOCIO */}
          {activeSection === "negocio" && (
            <section className="details-card" aria-label="Información del negocio">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Perfil</p>
                  <h2>Información del negocio</h2>
                </div>
              </div>
              <p className="rep-section-desc">
                Estos datos se utilizan para personalizar las respuestas automáticas y dar contexto a tu Agente IA.
              </p>

              <div className="rep-settings-context-row">
                <div>
                  <span>Espacio de ClickDialog</span>
                  <strong>{organization.organization.displayName}</strong>
                </div>
                <button
                  type="button"
                  className="rep-platform-action"
                  onClick={() => navigate(`/o/${organization.organization.slug}/settings`)}
                >
                  Administrar organización →
                </button>
              </div>

              <form onSubmit={handleSaveBusiness}>
                <div className="rep-field-grid">
                  <div className="field">
                    <label htmlFor="cfg-business-name">Nombre visible del local</label>
                    <input
                      id="cfg-business-name"
                      type="text"
                      placeholder="Ej: Café Aurora Central"
                      value={businessDisplayName}
                      onChange={(e) => setBusinessDisplayName(e.target.value)}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="cfg-address">Dirección física</label>
                    <input
                      id="cfg-address"
                      type="text"
                      placeholder="Ej: Av. Corrientes 1234, CABA"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="cfg-phone">Teléfono de atención</label>
                    <input
                      id="cfg-phone"
                      type="text"
                      placeholder="Ej: 011 4567-8900"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="cfg-whatsapp">WhatsApp de contacto</label>
                    <input
                      id="cfg-whatsapp"
                      type="text"
                      placeholder="Ej: +54 9 11 1234 5678"
                      value={whatsapp}
                      onChange={(e) => setWhatsapp(e.target.value)}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="cfg-website">Sitio web oficial</label>
                    <input
                      id="cfg-website"
                      type="url"
                      placeholder="https://..."
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                    />
                  </div>
                </div>

                <div className="button-row" style={{ marginTop: "24px" }}>
                  <button type="submit" className="primary-button" disabled={busy}>
                    {busy ? "Guardando…" : "Guardar información"}
                  </button>
                </div>
              </form>
            </section>
          )}

          {/* 2. GOOGLE */}
          {activeSection === "google" && (
            <section className="details-card" aria-label="Google Business Profile">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Integración</p>
                  <h2>Google Business Profile</h2>
                </div>
                <span className={`rep-status-pill ${isConnected ? "ok" : "warn"}`}>
                  <span className="rep-status-dot" aria-hidden="true" />
                  {googleConnectionLabel}
                </span>
              </div>

              <div className="rep-google-card-body">
                <div className="settings-note rep-google-connection-truth" role="status">
                  <strong>{isConnected ? "Cuenta de Google conectada" : googleConnectionLabel}</strong>
                  <p>
                    {isConnected
                      ? googleLocationsCount > 0
                        ? `${googleLocationsCount} ${googleLocationsCount === 1 ? "ficha detectada" : "fichas detectadas"} en esta consulta. La conexión de la cuenta y la selección de la ficha del negocio son pasos distintos.`
                        : "La cuenta está conectada, pero no se encontraron fichas en esta consulta. Revisá la cuenta y sus permisos antes de sincronizar; no hace falta conectar Google de nuevo."
                      : "Todavía no se confirmó una conexión activa. Las reseñas de Google no se pueden sincronizar desde esta pantalla hasta que la autorización esté vigente."}
                  </p>
                  {isConnected && (
                    <p className="rep-hint-text">
                      {googleAccountsCount} {googleAccountsCount === 1 ? "cuenta" : "cuentas"} informadas por Google · {googleLocationsCount} {googleLocationsCount === 1 ? "ubicación" : "ubicaciones"} detectadas.
                    </p>
                  )}
                </div>
                <div className="rep-field-grid">
                  <div className="field">
                    <label>Cuenta de Google vinculada</label>
                    <p className="rep-static-value">
                      {initialGoogleStatus?.google_user_email
                        || (isConnected ? "Cuenta conectada" : "No hay cuenta conectada")}
                    </p>
                  </div>

                  <div className="field">
                    <label>Fecha de vinculación</label>
                    <p className="rep-static-value">
                      {initialGoogleStatus?.connected_at
                        ? new Date(initialGoogleStatus.connected_at).toLocaleDateString("es-AR")
                        : (isConnected ? "Conexión activa" : "—")}
                    </p>
                  </div>
                </div>

                <div className="button-row" style={{ marginTop: "20px" }}>
                  {!isConnected ? (
                    <button
                      type="button"
                      className="primary-button"
                      disabled={busy}
                      onClick={handleGoogleConnect}
                    >
                      {busy ? "Conectando…" : "Conectar Google Business Profile"}
                    </button>
                  ) : (
                    <>
                      <button
                        type="button"
                        className="primary-button"
                        disabled={busy}
                        onClick={handleGoogleSync}
                      >
                        {busy ? "Sincronizando…" : "Sincronizar reseñas ahora"}
                      </button>
                      <button
                        type="button"
                        className="danger-button"
                        disabled={busy}
                        onClick={handleGoogleDisconnect}
                      >
                        Desconectar cuenta
                      </button>
                    </>
                  )}
                </div>

                {/* List of accounts and locations if connected */}
                {isConnected && (
                  <div className="rep-google-locations-list">
                    <h3>Fichas y ubicaciones detectadas</h3>
                    {initialGoogleLocations.length === 0 ? (
                      <p className="rep-hint-text">No se encontraron ubicaciones asociadas en esta consulta. La conexión de la cuenta sigue activa; verificá sus permisos y las fichas disponibles.</p>
                    ) : (
                      <div className="rep-locations-grid">
                        {initialGoogleLocations.map((loc) => (
                          <div key={loc.location_id} className="rep-location-badge">
                            <strong>{loc.title}</strong>
                            <span>{loc.address || "Ubicación activa"}</span>
                            {loc.is_primary && <span className="rep-primary-tag">Principal</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* 3. NOTIFICACIONES */}
          {activeSection === "notificaciones" && (
            <section className="details-card" aria-label="Notificaciones de reputación">
              <div className="section-heading">
                <div>
                  <p className="eyebrow">Alertas</p>
                  <h2>Notificaciones de Review Boost</h2>
                </div>
              </div>
              <p className="rep-section-desc">
                Elegí si querés recibir avisos inmediatos cuando ingresen reseñas negativas o delicadas.
              </p>

              <form onSubmit={handleSaveNotifications}>
                <div className="rep-toggle-item rep-toggle-card">
                  <input
                    id="cfg-alerts-toggle"
                    type="checkbox"
                    checked={alertsEnabled}
                    onChange={(e) => setAlertsEnabled(e.target.checked)}
                  />
                  <label htmlFor="cfg-alerts-toggle">
                    <strong>Alertas de reputación activas</strong>
                    <small>Avisos por email cuando se detecten calificaciones de 1–2★ o riesgo de negocio.</small>
                  </label>
                </div>

                <div className="field" style={{ marginTop: "20px" }}>
                  <label htmlFor="cfg-notif-email">Email para alertas de reputación</label>
                  <input
                    id="cfg-notif-email"
                    type="email"
                    placeholder="alertas@tunegocio.com"
                    value={notificationEmail}
                    onChange={(e) => setNotificationEmail(e.target.value)}
                  />
                </div>

                <div className="button-row" style={{ marginTop: "24px" }}>
                  <button type="submit" className="primary-button" disabled={busy}>
                    {busy ? "Guardando…" : "Guardar preferencias"}
                  </button>
                </div>
              </form>
            </section>
          )}

        </section>
      </div>
    </div>
  );
}